import * as React from 'react';
import { isNil, pathOr } from 'ramda';

import { IBaseQuestionProps, TAnswersState } from '../FormContainer/types/internal';
import { TValidators, useSimpleForm } from '../../utils/useSimpleForm';
import { QuestionLayout } from '../../components/QuestionLayout';
import { isEmptyString, isValidNumber } from '../../utils/validators';
import { PriceRangeQuestion } from '../../components/PriceQuestion';
import { formatPrice, getNumberString, isValidPrice, usePriceOptions } from './utils';
import { PriceEstimationContainer } from '../PriceEstimationContainer';
import { pickOnlyMeaning } from '../../utils/pickOnlyMeaning';
import { useFormTracking } from '../../utils/useFormTracking';

interface IRawPriceRangeQuestionProps<T = string> extends IBaseQuestionProps {
  validators: TValidators<T>;
}
export const RawPriceRangeQuestionContainer: React.FC<React.PropsWithChildren<IRawPriceRangeQuestionProps>> = props => {
  const {
    validators,
    question: { answerField, unit, title, required },
    setAnswersState,
    setNextStep,
    answersState,
  } = props;

  const { trackSubmit, trackChange, trackLteWithoutGte } = useFormTracking();
  const { selectedOptions, options, handleSelect } = usePriceOptions(props);

  const {
    handleSubmit,
    handleChange: onChange,
    values,
    errors,
    isSubmitDisabled,
  } = useSimpleForm<string>({
    onSubmit(values) {
      setAnswersState(prevState => {
        const gte = isValidPrice(values['gte']) ? Number(getNumberString(values['gte'])) : null;

        const lte = isValidPrice(values['lte']) ? Number(getNumberString(values['lte'])) : null;

        trackSubmit({
          isRequired: required,
          field: answerField,
          value: `${gte}-${lte}`,
        });

        const nextAnswersState: TAnswersState = {
          ...prevState,
          [answerField]: {
            answerField,
            value: {
              gte,
              lte,
              options: selectedOptions,
            },
          },
        };

        setNextStep(nextAnswersState);

        return nextAnswersState;
      });
    },
    validators,
    initialValues: pickOnlyMeaning({
      gte: formatPrice(pathOr('', [answerField, 'value', 'gte'], answersState)),
      lte: formatPrice(pathOr('', [answerField, 'value', 'lte'], answersState)),
    }),
    required,
  });

  const handleChange = React.useCallback<NonNullable<React.ComponentProps<'input'>['onChange']>>(
    e => {
      trackChange();

      const value = e.target.value;

      if (e.target.name === 'lte' && !values['gte']) {
        trackLteWithoutGte({
          isRequired: required,
          field: answerField,
          value,
        });
      }

      if (isNil(value) || isEmptyString(value)) {
        return onChange(e.target.name, value);
      }

      const numberValue = getNumberString(value);
      if (isValidNumber(numberValue)) {
        return onChange(e.target.name, formatPrice(numberValue));
      }
    },
    [onChange, trackLteWithoutGte, trackChange, answerField, required, values],
  );

  return (
    <QuestionLayout
      title={title}
      onSubmit={handleSubmit}
      footer={<PriceEstimationContainer />}
      disableSubmit={isSubmitDisabled}
    >
      <PriceRangeQuestion
        unit={unit}
        gteValue={pathOr('', ['gte'], values)}
        gteError={errors['gte']}
        gtePlaceholder={'от'}
        lteValue={pathOr('', ['lte'], values)}
        lteError={errors['lte']}
        ltePlaceholder={'до'}
        handleChange={handleChange}
        onSelect={handleSelect}
        selectedOptions={selectedOptions}
        options={options}
        lteInputName={`${answerField}_lte`}
        gteInputName={`${answerField}_gte`}
      />
    </QuestionLayout>
  );
};

RawPriceRangeQuestionContainer.displayName = 'RawPriceRangeQuestionContainer';
